import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BangladeshFlag, SwedenFlag } from "@/components/Flags";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c9f69bc0-91ae-40a1-9da9-440da68b284c/artifacts/xyedl7ct_IMG_8766.png";

const nav = [
  { to: "/", label: "Home" },
  { to: "/flights", label: "Flights" },
  { to: "/services", label: "Services" },
  { to: "/pay", label: "Pay" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#07090B] text-[color:var(--ivory)]">
      <header
        className={`sticky top-0 z-40 transition-all ${
          scrolled
            ? "backdrop-blur-xl bg-[#07090B]/75 border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="public-header"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 h-20 sm:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <span className="w-[54px] h-[54px] sm:w-[66px] sm:h-[66px] rounded-full bg-white flex items-center justify-center overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.45)]">
              <img src={LOGO_URL} alt="BDTT" className="w-[90%] h-[90%] object-contain logo-img" />
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-base text-white">Bangladesh Travels &amp; Tours AB</span>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)]">
                Dhaka · Stockholm
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition ${
                    isActive
                      ? "text-white"
                      : "text-[color:var(--ivory-muted)] hover:text-white"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contact"
              className="pill pill-primary text-sm"
              data-testid="header-cta"
            >
              Plan your trip
            </Link>
          </div>

          <button
            className="md:hidden p-2 rounded-md text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/5 bg-[#07090B]" data-testid="mobile-menu">
            <div className="px-5 py-3 flex flex-col gap-3">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `text-base py-2 ${
                      isActive ? "text-white" : "text-[color:var(--ivory-muted)]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="pill pill-primary text-sm text-center">
                Plan your trip
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Bangladesh flag colour band */}
      <div className="bdtt-flag-band" data-testid="flag-band">
        <div className="bdtt-flag-band-red" />
        <div className="bdtt-flag-band-green" />
      </div>

      <footer className="border-t border-white/5 bg-[#0A0D11]" data-testid="public-footer">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                <img src={LOGO_URL} alt="BDTT" className="w-[90%] h-[90%] object-contain logo-img" />
              </span>
              <span className="font-display text-lg">Bangladesh Travels &amp; Tours AB</span>
            </div>
            <p className="mt-4 text-sm text-[color:var(--ivory-muted)] max-w-md leading-relaxed">
              Connecting Bangladesh and Sweden — and the rest of the world — through
              handcrafted tours, seamless flights, and unforgettable journeys.
            </p>
            <div className="mt-6 flex items-center gap-4 text-sm">
              <BangladeshFlag className="w-7 h-5 rounded-sm" />
              <span className="text-[color:var(--ivory-muted)]">Dhaka</span>
              <span className="text-[color:var(--ivory-muted)]">·</span>
              <SwedenFlag className="w-7 h-5 rounded-sm" />
              <span className="text-[color:var(--ivory-muted)]">Stockholm</span>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--bd-green-bright)] mb-4">Company</div>
            <ul className="space-y-2 text-sm text-[color:var(--ivory-muted)]">
              <li className="text-white font-medium">Bangladesh Travels &amp; Tours AB</li>
              <li>
                <span className="text-[color:var(--ivory-muted)]">Org. nr</span>{" "}
                <span className="text-white font-mono">559562-4361</span>
              </li>
              <li className="leading-relaxed pt-1">
                Kråksätrabacken 27<br />
                127 30 Skärholmen<br />
                Sweden
              </li>
              <li className="pt-1">
                <a href="mailto:info@bdtt.se" className="text-white hover:text-[color:var(--bd-green-bright)]">info@bdtt.se</a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--bd-red-bright)] mb-4">Explore</div>
            <ul className="space-y-2 text-sm">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-white text-[color:var(--ivory-muted)]">
                    {n.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 border-t border-white/5 mt-3">
                <Link to="/admin/login" className="text-[color:var(--ivory-muted)] hover:text-white text-xs">
                  Admin sign in
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 px-5 sm:px-8 lg:px-12 text-xs text-[color:var(--ivory-muted)] flex flex-col sm:flex-row items-center justify-between gap-2 max-w-[1400px] mx-auto">
          <div>© {new Date().getFullYear()} Bangladesh Travels &amp; Tours AB · All rights reserved</div>
          <div>Org. nr 559562-4361 · Sweden &amp; Bangladesh</div>
        </div>
      </footer>
    </div>
  );
}
